import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { finalize, map, switchMap, take, catchError } from 'rxjs/operators';
import { AqlStoreFacade } from './aql-db.facade';
import {
  AcquireLockAndThenInitAqlStore,
  AqlNoAction,
  AquireLockAndDeleteAudit,
  AquireLockAndUpdateWeeklyfailedEvents,
  AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList,
  AquireLockUpdateSavedEventsAndrecentCateGoryList,
  InitAqlStoreAndThenReleaseLock,
  NoAqlAction,
  TAQLStoreActionsType,
  UpdateAqlStoreAndReleaseLock,
  UpdateAqlStoreAndReleaseLockAndFetchWeeklyFailedEvents,
  UpdateWeeklyfailedEventsAndReleaseLock,
} from './aql-db.actions';

import * as AqlActions from './aql-db.actions';
import { AqlAppDataService, CustomLoggerService, InputAppDataService } from '@monorepo/services';
import { InputAppAPIHelperUtil } from '@monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { InitStoreFacade } from '../init-db/init-db.facade';
import { SAVED_EVENTS_CACHE_STORAGE_TIME, getAqlStoreInitalState } from './aql-db.reducer';
import { AqlUtil } from '@monorepo/utils';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import produce from 'immer';
import { TAQLAudit, TBCTXKeysWithoutSize } from '@monorepo/types';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class AqlStoreEffects {
  private readonly COMPONENT_NAME = 'AqlStoreEffects';

  constructor(
    private action$: Actions,
    private aqlStoreFacade: AqlStoreFacade,
    private aqlAppDataService: AqlAppDataService,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private translateService: TranslateService,
    private persistentStorageService: PersistentStorageService,
    private initStoreFacade: InitStoreFacade,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private inputAppDataService: InputAppDataService,
    private customLoggerService: CustomLoggerService,
  ) {}

  AcquireLockAndThenInitStore$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndThenInitAqlStore>(TAQLStoreActionsType.AcquireLockAndThenInitAqlStore),
      switchMap((action) => {
        return this.aqlStoreFacade.acquireLock(action.lockFor).pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new InitAqlStoreAndThenReleaseLock();
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return new AqlNoAction();
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error acquiring lock`,
              JSON.stringify(action),
              JSON.stringify(error),
            );
            return of(new AqlNoAction());
          }),
        );
      }),
    );
  });

  InitStoreAndThenReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<InitAqlStoreAndThenReleaseLock>(TAQLStoreActionsType.InitAqlStoreAndThenReleaseLock),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheRestTimestamp) => [nextCacheRestTimestamp] as const),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting cache reset timestamp`,
              JSON.stringify(error),
            );
            return of([Date.now()] as const);
          }),
        );
      }),
      switchMap(([nextCacheRestTimestamp]) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_AQL_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (res && res.nextCacheResetTimestamp === nextCacheRestTimestamp)
              return of(new UpdateAqlStoreAndReleaseLock(res));
            else {
              const aqlInitialState = getAqlStoreInitalState(nextCacheRestTimestamp);
              let updatedAqlState = aqlInitialState.aqlDB;
              if (res) {
                try {
                  const currentTime = new Date().getTime();
                  Object.entries(res.persistanceCache.savedEvents.lineMap).forEach(
                    ([line, shiftWiseSavedEventsCache]) => {
                      Object.entries(shiftWiseSavedEventsCache.shiftMap).forEach(([shift, savedEventDataList]) => {
                        savedEventDataList.forEach((savedEvent) => {
                          if (savedEvent.metaData.datetime + SAVED_EVENTS_CACHE_STORAGE_TIME >= currentTime) {
                            updatedAqlState = produce(updatedAqlState, (draft) => {
                              if (!draft.persistanceCache.savedEvents.lineMap[line]) {
                                draft.persistanceCache.savedEvents.lineMap[line] = {
                                  shiftMap: {
                                    [shift]: [savedEvent],
                                  },
                                };
                              } else if (!draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift]) {
                                draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift] = [savedEvent];
                              } else draft.persistanceCache.savedEvents.lineMap[line].shiftMap[shift].push(savedEvent);
                            });
                          }
                        });
                      });
                    },
                  );
                } catch (error) {
                  this.customLoggerService.log(
                    `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error processing saved events`,
                    JSON.stringify(res),
                    JSON.stringify(error),
                  );
                }
              }
              return from(this.persistentStorageService.set('[APP][INPUT_APP_AQL_DB]', updatedAqlState)).pipe(
                take(1),
                map(() => {
                  return new UpdateAqlStoreAndReleaseLockAndFetchWeeklyFailedEvents(updatedAqlState);
                }),
                catchError((error) => {
                  this.customLoggerService.log(
                    `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error setting persistent storage`,
                    JSON.stringify(error),
                  );
                  return of(new AqlNoAction());
                }),
              );
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting persistent storage`,
              JSON.stringify(error),
            );
            return of(new AqlNoAction());
          }),
        ),
      ),
    );
  });

  UpdateAqlStoreAndReleaseLockAndFetchWeeklyFailedEvents$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AqlActions.UpdateAqlStoreAndReleaseLockAndFetchWeeklyFailedEvents>(
        TAQLStoreActionsType.UpdateAqlStoreAndReleaseLockAndFetchWeeklyFailedEvents,
      ),
      switchMap(() => {
        this.aqlStoreFacade.fetchWeeklyFailedEvents();
        return of(new NoAqlAction());
      }),
    );
  });

  AquireLockAndUpdateWeeklyfailedEvents$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AquireLockAndUpdateWeeklyfailedEvents>(TAQLStoreActionsType.AquireLockAndUpdateWeeklyfailedEvents),
      switchMap((action) => {
        return this.aqlStoreFacade.acquireLock('UPDATE_WEEKLY_FAILED_EVENTS').pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new UpdateWeeklyfailedEventsAndReleaseLock(action.data);
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return new AqlNoAction();
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error acquiring lock for weekly failed events`,
              JSON.stringify(action),
              JSON.stringify(error),
            );
            return of(new AqlNoAction());
          }),
        );
      }),
    );
  });

  UpdateWeeklyfailedEventsAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateWeeklyfailedEventsAndReleaseLock>(TAQLStoreActionsType.UpdateWeeklyfailedEventsAndReleaseLock),
      switchMap((action) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_AQL_DB]')).pipe(
          take(1),
          switchMap((aqlDB) => {
            try {
              const mappedAqlAuditBackendList = AqlUtil.getAqlAuditMappedDto(action.data);
              const frontendList = AqlUtil.getAllLinesFrontendMappedAqlList(aqlDB.dailyCache.completedEvents);
              const backendAQLAuditWithAuditIdGrouping = AqlUtil.getAqlGroupDataAfterMergingfrontendAndBackendData(
                frontendList,
                mappedAqlAuditBackendList,
              );
              const aqlListAfterRemovingUndo = AqlUtil.getLastAuditDataAfterUndo(backendAQLAuditWithAuditIdGrouping);

              let latestFailedAuditListingItems: TAQLAudit[] = [];

              const reAuditIdGroups = AqlUtil.getReAuditIdGroups(aqlListAfterRemovingUndo);
              Object.values(reAuditIdGroups).forEach((auditList) => {
                auditList.length && latestFailedAuditListingItems.push(auditList[0]);
              });

              let updatedAqlState = produce(aqlDB, (draft) => {
                draft.dailyCache.currentWeekFailedEvents = latestFailedAuditListingItems;
              });
              return from(this.persistentStorageService.set('[APP][INPUT_APP_AQL_DB]', updatedAqlState)).pipe(
                take(1),
                map(() => {
                  return new UpdateAqlStoreAndReleaseLock(updatedAqlState);
                }),
                catchError((error) => {
                  this.customLoggerService.log(
                    `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error setting persistent storage for weekly failed events`,
                    JSON.stringify(error),
                  );
                  return of(new AqlNoAction());
                }),
              );
            } catch (error) {
              this.customLoggerService.log(
                `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error processing weekly failed events`,
                JSON.stringify(action),
                JSON.stringify(error),
              );
              return of(new AqlNoAction());
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting persistent storage for weekly failed events`,
              JSON.stringify(error),
            );
            return of(new AqlNoAction());
          }),
        );
      }),
    );
  });

  getAuditListingMappedWithLocalStorage$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AqlActions.GetAuditListingMappedWithLocalStorage>(
        TAQLStoreActionsType.GetAuditListingMappedWithLocalStorage,
      ),
      switchMap((action) => {
        if (action.showLoader) {
          return from(this.inputAppAPIHelperUtil.presentLoading('Fetching Data')).pipe(
            take(1),
            map(() => action),
            catchError((error) => {
              this.customLoggerService.log(
                `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error presenting loader`,
                JSON.stringify(action),
                JSON.stringify(error),
              );
              return of(action);
            }),
          );
        } else {
          return of(action);
        }
      }),
      switchMap((action) => {
        return this.aqlStoreFacade.getCompletedEventsForSelectedLineAndShift().pipe(
          take(1),
          map((completedEvents) => {
            return [completedEvents, action] as const;
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting completed events`,
              JSON.stringify(action),
              JSON.stringify(error),
            );
            return of([Array.from([]) as TAQLAudit[], action] as const);
          }),
        );
      }),
      switchMap(([completedEvents, action]) => {
        return from(this.aqlAppDataService.getAqlListingPagesLogs(action.payload)).pipe(
          take(1),
          switchMap((res) => {
            try {
              if (res) {
                if (
                  action.listingType === 'ACTIVITY_HISTORY' ||
                  action.listingType === 'REAUDIT' ||
                  action.listingType === 'ACTION_HISTORY'
                ) {
                  const mappedAqlAuditBackendList = AqlUtil.getAqlAuditMappedDto(res.hbase_logs_query_request.data);
                  const allLinesFrontendAqlList = completedEvents;

                  const frontendBackendMergedAqlListWithUndoOperation =
                    AqlUtil.getAqlGroupDataAfterMergingfrontendAndBackendData(
                      allLinesFrontendAqlList,
                      mappedAqlAuditBackendList,
                    );
                  const aqlListAfterRemovingUndo = AqlUtil.getLastAuditDataAfterUndo(
                    frontendBackendMergedAqlListWithUndoOperation,
                  );
                  const allFrontendEventsMappedWithAuditId =
                    AqlUtil.groupAQLAuditDataBasedOnAuditId(allLinesFrontendAqlList);
                  const allFrontendEventsAfterUndo = AqlUtil.getLastAuditDataAfterUndo(
                    allFrontendEventsMappedWithAuditId,
                  );
                  if (action.listingType === 'ACTIVITY_HISTORY') {
                    this.aqlStoreFacade.emitListingPagesSubscription({
                      pageType: 'ACTIVITY_HISTORY',
                      data: aqlListAfterRemovingUndo,
                      frontendEventsAfterUndo: allFrontendEventsAfterUndo,
                    });
                  } else if (action.listingType === 'ACTION_HISTORY') {
                    this.aqlStoreFacade.emitListingPagesSubscription({
                      pageType: 'ACTION_HISTORY',
                      data: frontendBackendMergedAqlListWithUndoOperation,
                      frontendEventsAfterUndo: {},
                    });
                  } else {
                    this.aqlStoreFacade.emitListingPagesSubscription({
                      pageType: 'REAUDIT',
                      data: AqlUtil.getReAuditIdGroups(aqlListAfterRemovingUndo),
                      frontendEventsAfterUndo: {},
                    });
                  }

                  return of(new AqlActions.AqlNoAction());
                } else {
                  return of(new AqlActions.AqlNoAction());
                }
              } else {
                return of(new AqlActions.AqlNoAction());
              }
            } catch (error) {
              this.customLoggerService.log(
                `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error processing audit listing data`,
                JSON.stringify({ action, res }),
                JSON.stringify(error),
              );
              return of(new AqlActions.AqlNoAction());
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting AQL listing pages logs`,
              JSON.stringify(error),
            );
            return of(new AqlActions.AqlNoAction());
          }),
          finalize(() => {
            if (action.showLoader) {
              this.inputAppAPIHelperUtil.dismissLoading().catch((error) => {
                this.customLoggerService.log(
                  `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error dismissing loader`,
                  JSON.stringify(error),
                );
              });
            }
          }),
        );
      }),
    );
  });

  AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList>(
        TAQLStoreActionsType.AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList,
      ),
      switchMap((action) => {
        return from(this.inputAppAPIHelperUtil.presentLoading(action.loaderMessage)).pipe(
          take(1),
          map(() => action),
        );
      }),
      switchMap((action) => {
        return this.aqlStoreFacade.getBulkEvent(action.singleAuditDetail).pipe(
          take(1),
          map((bulkEvent) => [action, bulkEvent] as const),
        );
      }),
      switchMap(([action, bulkEvent]) => {
        const { singleAuditDetail } = action;
        return this.aqlStoreFacade.getCompletedEventsForSelectedLineAndShift().pipe(
          take(1),
          switchMap((completedAudits) => {
            // Skip validation for undo_audit action
            const isUndo = singleAuditDetail.metaData.action_taken === 'undo_audit';
            const isDuplicate =
              !isUndo &&
              completedAudits.some(
                (audit) =>
                  audit.metaData.auditId === singleAuditDetail.metaData.auditId &&
                  audit.metaData.reAuditId === singleAuditDetail.metaData.reAuditId &&
                  audit.metaData.auditCycle === singleAuditDetail.metaData.auditCycle,
              );
            if (isDuplicate) {
              this.customLoggerService.log(
                "[WHAT'S_WRONG]: AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList - Duplicate audit detected",
                JSON.stringify(action.singleAuditDetail),
                JSON.stringify(completedAudits),
              );
              this.inputAppAPIHelperUtil.showPopup(
                this.translateService.instant('common.error'),
                `Audit already exists: AuditID=${singleAuditDetail.metaData.auditId}, ReAuditID=${singleAuditDetail.metaData.reAuditId}, Cycle=${singleAuditDetail.metaData.auditCycle}`,
              );
              return of(new AqlNoAction());
            }
            return this.aqlStoreFacade.acquireLock('SAVE_COMPLETED_EVENT').pipe(
              take(1),
              switchMap((lockResult) => {
                if (lockResult.status !== 'SUCCESS') {
                  const errorMessage = `${lockResult.message} - Try again later from Audits Saved.`;
                  this.inputAppAPIHelperUtil.showPopup(
                    this.translateService.instant('common.error'),
                    errorMessage,
                  );
                  this.customLoggerService.log(
                    "[WHAT'S_WRONG]: AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList - Failed to acquire lock for completed events",
                    JSON.stringify(action.singleAuditDetail),
                    JSON.stringify(lockResult),
                  );
                  return of(new AqlNoAction());
                }
                return this.inputAppClientDBFacade
                  .getFullSaveEventPayload([bulkEvent], new Date().getTime())
                  .pipe(
                    take(1),
                    switchMap((payload) => {
                      return this.inputAppDataService.saveEvent(payload).pipe(
                        take(1),
                        switchMap(() => {
                          return this.aqlStoreFacade.getBctxItemsOrderWithoutSize().pipe(
                            take(1),
                            switchMap((orderKeys) => {
                              return from(this.persistentStorageService.get('[APP][INPUT_APP_AQL_DB]')).pipe(
                                take(1),
                                switchMap((currentDB) => {
                                  const updatedDB = AqlUtil.updateCompletedEventAndRecentCategory(
                                    currentDB,
                                    bulkEvent,
                                    orderKeys,
                                  );
                                  return from(
                                    this.persistentStorageService.set('[APP][INPUT_APP_AQL_DB]', updatedDB),
                                  ).pipe(
                                    take(1),
                                    map(() => new UpdateAqlStoreAndReleaseLock(updatedDB)),
                                  );
                                }),
                              );
                            }),
                          );
                        }),
                      );
                    }),
                    catchError((error) => {
                      this.aqlStoreFacade.releaseLock();
                      this.customLoggerService.log(
                        `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error saving event`,
                        JSON.stringify(singleAuditDetail),
                        JSON.stringify(error),
                      );
                      this.inputAppAPIHelperUtil.showPopup(
                        this.translateService.instant('common.error'),
                        'Something Went Wrong. Please try again from Audits Saved or contact support.',
                      );
                      this.aqlStoreFacade.aquireLockUpdateSavedEventsAndrecentCateGoryList(bulkEvent);
                      return of(new AqlNoAction());
                    }),
                  );
              }),
            );
          }),
          finalize(() => this.inputAppAPIHelperUtil.dismissLoading()),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Unexpected error in AquireLockSaveAndUpdateCompletedEventsAndrecentCateGoryList`,
              JSON.stringify(singleAuditDetail),
              JSON.stringify(error),
            );
            this.inputAppAPIHelperUtil.showPopup(
              this.translateService.instant('common.error'),
              'Something Went Wrong. Please try again from Audits Saved.',
            );
            this.aqlStoreFacade.aquireLockUpdateSavedEventsAndrecentCateGoryList(bulkEvent);
            return of(new AqlNoAction());
          }),
        );
      }),
    );
  });

  AquireLockUpdateSavedEventsAndrecentCateGoryList$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AquireLockUpdateSavedEventsAndrecentCateGoryList>(
        TAQLStoreActionsType.AquireLockUpdateSavedEventsAndrecentCateGoryList,
      ),
      switchMap((action) => {
        return this.inputAppClientDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map((res) => [action, res] as const),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting BCTX items order for saved events`,
              JSON.stringify(action),
              JSON.stringify(error),
            );
            return of([action, Array.from([]) as TBCTXKeysWithoutSize[]] as const);
          }),
        );
      }),
      switchMap(([action, orderKeys]) => {
        return this.aqlStoreFacade.acquireLock('SAVE_INPROGRESS_EVENT').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              return from(this.persistentStorageService.get('[APP][INPUT_APP_AQL_DB]')).pipe(
                take(1),
                switchMap((currentDB) => {
                  try {
                    const updatedDB = AqlUtil.updateSavedEventAndRecentCategory(currentDB, action.bulkEvent, orderKeys);
                    return from(this.persistentStorageService.set('[APP][INPUT_APP_AQL_DB]', updatedDB)).pipe(
                      take(1),
                      map(() => {
                        return new UpdateAqlStoreAndReleaseLock(updatedDB);
                      }),
                      catchError((error) => {
                        this.customLoggerService.log(
                          `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error setting persistent storage for saved events`,
                          JSON.stringify(error),
                        );
                        return of(new AqlNoAction());
                      }),
                    );
                  } catch (error) {
                    this.customLoggerService.log(
                      `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error updating saved events`,
                      JSON.stringify({ currentDB, action, orderKeys }),
                      JSON.stringify(error),
                    );
                    return of(new AqlNoAction());
                  }
                }),
                catchError((error) => {
                  this.customLoggerService.log(
                    `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting persistent storage for saved events`,
                    JSON.stringify(error),
                  );
                  return of(new AqlNoAction());
                }),
              );
            } else {
              this.customLoggerService.log(
                "[WHAT'S_WRONG]: AquireLockUpdateSavedEventsAndrecentCateGoryList - Failed to acquire lock for saved events",
                action.bulkEvent,
                JSON.stringify(res),
              );
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return of(new AqlNoAction());
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error acquiring lock for saved events`,
              JSON.stringify(action),
              JSON.stringify(error),
            );
            return of(new AqlNoAction());
          }),
        );
      }),
    );
  });

  AquireLockAndDeleteAudit$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AquireLockAndDeleteAudit>(TAQLStoreActionsType.AquireLockAndDeleteAudit),
      switchMap((action) => {
        return this.aqlStoreFacade.acquireLock('DELETE_AUDIT').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              return from(this.persistentStorageService.get('[APP][INPUT_APP_AQL_DB]')).pipe(
                take(1),
                switchMap((currentDB) => {
                  try {
                    const updatedDB = AqlUtil.deleteAudit(currentDB, action.aqlAuditMetaData);
                    return from(this.persistentStorageService.set('[APP][INPUT_APP_AQL_DB]', updatedDB)).pipe(
                      take(1),
                      map(() => {
                        return new UpdateAqlStoreAndReleaseLock(updatedDB);
                      }),
                      catchError((error) => {
                        this.customLoggerService.log(
                          `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error setting persistent storage for delete audit`,
                          JSON.stringify(error),
                        );
                        return of(new AqlNoAction());
                      }),
                    );
                  } catch (error) {
                    this.customLoggerService.log(
                      `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error deleting audit`,
                      JSON.stringify({ currentDB, action }),
                      JSON.stringify(error),
                    );
                    return of(new AqlNoAction());
                  }
                }),
                catchError((error) => {
                  this.customLoggerService.log(
                    `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error getting persistent storage for delete audit`,
                    JSON.stringify(error),
                  );
                  return of(new AqlNoAction());
                }),
              );
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translateService.instant('common.error'), res.message);
              return of(new AqlNoAction());
            }
          }),
          catchError((error) => {
            this.customLoggerService.log(
              `[WHAT'S_WRONG]: ${this.COMPONENT_NAME} - Error acquiring lock for delete audit`,
              JSON.stringify(action),
              JSON.stringify(error),
            );
            return of(new AqlNoAction());
          }),
        );
      }),
    );
  });
}
